import React, { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'

const classNameInUser = ' amber-100 amber-500 yellow-100 yellow-500 pink-100 pink-500'

const MenuItem = ({ item, isActive = false, setActiveSubmenu = () => {} }) => {
  const [hovered, setHovered] = useState(false)
  const menuRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState({ left: '50%', transform: 'translateX(-50%)' })
  const hasDropdown = Boolean(item.hasDropdown)

  // Calculate dropdown position to prevent it from going outside the screen
  useEffect(() => {
    if (!menuRef.current || !hasDropdown) return

    const calculatePosition = () => {
      const menuRect = menuRef.current.getBoundingClientRect()
      const dropdownWidth = parseInt(item.cardWidth || '600')
      const viewportWidth = window.innerWidth
      const padding = 20

      // Default centered position
      let leftPosition = '50%'
      let transform = 'translateX(-50%)'

      // Check if dropdown would go outside the left edge
      if (menuRect.left - dropdownWidth / 2 < padding) {
        leftPosition = `${Math.max(padding - menuRect.left, 0)}px`
        transform = 'none'
      }

      // Check if dropdown would go outside the right edge
      else if (menuRect.left + dropdownWidth / 2 > viewportWidth - padding) {
        const rightOffset = Math.max(menuRect.left + dropdownWidth / 2 - (viewportWidth - padding), 0)
        leftPosition = `calc(50% - ${rightOffset}px)`
      }

      setDropdownPosition({ left: leftPosition, transform })
    }

    calculatePosition()
    window.addEventListener('resize', calculatePosition)
    return () => window.removeEventListener('resize', calculatePosition)
  }, [hasDropdown, item.cardWidth, hovered, isActive])

  // Handle mouse events for the menu item
  const handleMouseEnter = () => {
    setHovered(true)
    if (hasDropdown) setActiveSubmenu(true)
  }

  const handleMouseLeave = () => {
    setHovered(false)
    if (hasDropdown) setActiveSubmenu(false)
  }

  return (
    <div className="relative group" ref={menuRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link
        href={item.href || '#'}
        className={cn(
          'text-theme-header-text transition-colors font-medium flex items-center py-2 px-1',
          'hover:text-theme-primary',
          isActive && 'text-theme-primary'
        )}
      >
        {item.icon && <item.icon className="w-4 h-4 mr-1" />}
        <span className="whitespace-nowrap text-sm 2xl:text-base">{item.label}</span>
        {hasDropdown && (
          <ChevronDown
            className={cn('ml-1 w-3.5 h-3.5 transition-transform duration-200', (isActive || hovered) && 'rotate-180')}
          />
        )}
      </Link>

      {/* Dropdown Menu Card */}
      {hasDropdown && (
        <AnimatePresence>
          {isActive && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.05 }}
              className={cn('absolute pt-4 z-20 w-auto', 'block', 'group-hover:block')}
              style={{
                width: item.cardWidth || '600px',
                maxWidth: 'calc(100vw - 40px)',
                left: dropdownPosition.left,
                transform: dropdownPosition.transform,
              }}
            >
              <motion.div
                className="bg-white shadow-xl rounded-lg border border-theme-border overflow-hidden backdrop-blur-lg backdrop-filter"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.05, delay: 0.05 }}
              >
                <item.component item={item} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}

export default MenuItem
